/**
 * Fuenf kurze Fragen nach der Registrierung: /welcome
 *
 * Ein Bildschirm je Frage, oben ein Segment je Frage. Zurueck links,
 * Ueberspringen rechts, jederzeit. Fragen, Schluessel und Ablauf stehen in
 * `../onboarding.ts` und sind dort getestet; hier steht nur die Darstellung.
 *
 * EINFACHAUSWAHL SPRINGT NICHT AUTOMATISCH WEITER. In einer Radiogruppe waehlt
 * man mit den Pfeiltasten; ein automatischer Sprung wuerde schon beim
 * Durchgehen ausloesen. Und „Etwas anderes" braucht danach noch das Textfeld.
 *
 * ANTWORTEN BEKOMMEN EINE KLEINE ANTWORT (seit 29.09.2026, Robert: das
 * Onboarding solle mehr unterhalten und weniger ueberfordern): die Chips
 * erscheinen gestaffelt, eine Auswahl federt kurz, darunter steht ein Satz
 * (`reaktion` in onboarding.ts). Bei reduzierter Bewegung nur der Satz.
 *
 * NACH DER LETZTEN FRAGE kein Dankeschoen als Sackgasse, sondern der
 * Rundgang als Geschichte (Einfuehrung.tsx): eine Headline, vier Szenen,
 * dann das Verbinden mit Claude in drei Schritten und das Guthaben. Dann
 * „Dein erster Run" unter /runs. Der Rundgang kommt auch
 * nach „Überspringen": die Fragen sind fuer uns, der Rundgang fuer den Nutzer.
 * „← Zurück" oben links fuehrt einen Bildschirm zurueck, vom ersten zur letzten Frage, nach
 * „Überspringen" zu der, von der uebersprungen wurde.
 * Beim Aendern der Antworten (?edit=1) kommt er nicht.
 *
 * Laesst sich nicht speichern (Tabelle fehlt, Netz weg), geht es trotzdem
 * weiter: niemand soll an einer Umfrage haengen bleiben.
 */

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ladeStatus, type Status } from '../api';
import {
  ablauf, anfang, ANDERES, FRAGEN, FREITEXT_MAX, fertig, frageReaktion, frageTitel, ladeOnboarding, optionText, speichereOnboarding, zumSpeichern, zurueckAusRundgang,
  type Aktion, type FrageId, type Gespeichert, type Stand,
} from '../onboarding';
import { ersterLaufVorschlag } from '../erster-lauf';
import { EINRICHTUNG, VERGLEICH_1ZU1, VERGLEICH_STANDARD } from '../preise';
import { useSitzung } from '../session';
import { supabase } from '../supabase';
import { sicheresZiel } from '../ziel';
import { Kreisel, Wortmarke } from '../ui';
import { Rundgang } from './Einfuehrung';
import { melde } from '../messung';
import { t } from '../sprache';

// ─── Einmal je Sitzung pruefen ──────────────────────────────────────────────
//
// Das Ergebnis haengt am Nutzer, nicht an der Seite: wer zwischen Konto und
// Laeufen wechselt, soll nicht jedes Mal eine Abfrage abwarten.

let bekannt: { nutzer: string; zustand: 'offen' | 'erledigt' | 'aus'; antworten?: Gespeichert } | null = null;

const merke = (nutzer: string, zustand: 'offen' | 'erledigt' | 'aus', antworten?: Gespeichert) => { bekannt = { nutzer, zustand, antworten }; };

/** Die Antworten dieser Sitzung, fuer „Dein erster Lauf". Ohne Antworten oder ohne Tabelle: nichts. */
export const bekannteAntworten = (nutzer: string | undefined): Gespeichert | null =>
  nutzer && bekannt?.nutzer === nutzer ? bekannt.antworten ?? null : null;

/** Fuer den Link auf dem Konto: nur zeigen, wenn sich Antworten speichern lassen. */
export const fragenVerfuegbar = (nutzer: string | undefined) => !!nutzer && bekannt?.nutzer === nutzer && bekannt.zustand !== 'aus';

/**
 * Vor die angemeldeten Seiten gestellt. Hat das Konto noch keine Zeile, geht
 * es einmal zu den Fragen; das Ziel wird mitgegeben. Fehlt die Tabelle, geht es
 * ohne Fragen weiter.
 */
export function MitFragen({ children }: { children: ReactNode }) {
  const { session } = useSitzung();
  const nutzer = session?.user?.id ?? session?.user?.email ?? '';
  const ort = useLocation();
  const [zustand, setZustand] = useState(bekannt?.nutzer === nutzer ? bekannt.zustand : null);

  useEffect(() => {
    if (!nutzer || bekannt?.nutzer === nutzer) return;
    let lebt = true;
    void ladeOnboarding(supabase).then(z => {
      merke(nutzer, z.zustand, z.zustand === 'erledigt' ? z.antworten : undefined);
      if (lebt) setZustand(z.zustand);
    });
    return () => { lebt = false; };
  }, [nutzer]);

  if (!zustand) {
    return (
      <main className="grid min-h-dvh place-items-center text-text-hell" role="status" aria-label={t('Einen Moment', 'One moment')}>
        <Kreisel className="size-6" />
      </main>
    );
  }
  if (zustand === 'offen') {
    return <Navigate to={`/welcome?next=${encodeURIComponent(ort.pathname + ort.search + ort.hash)}`} replace />;
  }
  return <>{children}</>;
}

// ─── Die Seite ──────────────────────────────────────────────────────────────

export default function Fragen() {
  const { session } = useSitzung();
  const nutzer = session?.user?.id ?? session?.user?.email ?? '';
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const aendern = params.get('edit') === '1';
  const naechstes = params.get('next');

  const [stand, setStand] = useState<Stand | null>(null);
  const [aus, setAus] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  /** Ziel nach dem Rundgang. Gesetzt heisst: die Fragen sind durch, der Rundgang steht. */
  const [rundgang, setRundgang] = useState<string | null>(null);
  /** Wie die Fragen endeten: nach „Überspringen" begruesst der Rundgang ohne „Danke.". */
  const [uebersprungen, setUebersprungen] = useState(false);
  /** Credits fuer den letzten Bildschirm des Rundgangs; ohne Zahl fehlt der Kasten. */
  const [guthaben, setGuthaben] = useState<number | null>(null);
  const status = useRef<Promise<Status | null> | null>(null);
  /** onboarding_done nur einmal, auch wenn jemand aus dem Rundgang zurueckgeht und neu abschliesst. */
  const gemeldet = useRef(false);
  const titel = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = t('Ein paar Fragen · 99takes', 'A few questions · 99takes');
    // Die Konto-id brauchen wir erst beim Speichern; billing/status legt die
    // Kontozeile an, falls sie nach der Bestaetigung noch fehlt.
    status.current = ladeStatus().catch(e => { console.warn('[onboarding] Konto', e); return null; });
    let lebt = true;
    void ladeOnboarding(supabase).then(z => {
      if (!lebt) return;
      if (z.zustand === 'aus') { setAus(true); return; }
      setStand(anfang(z.zustand === 'erledigt' ? z.antworten : null));
    });
    return () => { lebt = false; };
  }, []);

  // Beim Wechsel der Frage den Titel fokussieren: ein Screenreader liest dann
  // die neue Frage vor, und Tab beginnt oben statt beim alten Knopf.
  useEffect(() => {
    if (stand && !fertig(stand) && !rundgang) titel.current?.focus({ preventScroll: true });
  }, [stand?.schritt, rundgang]);

  // Dorthin, wo jemand hinwollte, sonst zum Standard /runs („Dein erster Run").
  // Bis zum 29.09.2026 war der Standard /account, und hier stand eine Ausnahme,
  // die /account nach der letzten Frage auf /runs umbog. Mit /runs als Standard
  // ist sie überflüssig, und ein bewusstes /account (Rückkehr von Mollie mit
  // ?checkout=) kommt jetzt auch dort an.
  const ziel = () => sicheresZiel(naechstes);

  async function abschliessen(s: Stand, art: 'fertig' | 'uebersprungen') {
    setSpeichert(true);
    const s0 = await status.current;
    const id = s0?.account_id ?? null;
    const ok = id ? await speichereOnboarding(supabase, id, zumSpeichern(s), art) : false;
    // Ohne Speichern trotzdem weiter. Gemerkt wird dann „aus", damit die
    // Fragen in dieser Sitzung nicht noch einmal kommen.
    if (nutzer) merke(nutzer, ok ? 'erledigt' : 'aus', ok ? zumSpeichern(s) : undefined);
    if (!aendern && !gemeldet.current) { gemeldet.current = true; melde({ event: 'onboarding_done', art }); }
    if (aendern) { navigate(ziel(), { replace: true }); return; }
    setSpeichert(false);
    setGuthaben(s0?.balance?.metered && typeof s0.balance.balance === 'number' ? s0.balance.balance : null);
    setUebersprungen(art === 'uebersprungen');
    setRundgang(ziel());
    window.scrollTo({ top: 0 });
  }

  useEffect(() => {
    document.title = rundgang
      ? t('So arbeitest du mit 99takes · 99takes', 'How you work with 99takes · 99takes')
      : t('Ein paar Fragen · 99takes', 'A few questions · 99takes');
  }, [rundgang]);

  if (rundgang) {
    return (
      <Rundgang
        ziel={rundgang}
        guthaben={guthaben}
        uebersprungen={uebersprungen}
        weiter={() => navigate(rundgang, { replace: true })}
        zurueck={() => {
          // Zurueck zur letzten Frage (nach „Fertig") oder zu der, von der
          // uebersprungen wurde. Die Antworten stehen noch in `stand`; beim
          // zweiten Abschliessen ueberschreibt das upsert die Zeile.
          setStand(s => (s ? zurueckAusRundgang(s) : s));
          setRundgang(null);
          window.scrollTo({ top: 0 });
        }}
      />
    );
  }

  if (aus) return <Navigate to={sicheresZiel(naechstes)} replace />;
  if (!stand) {
    return (
      <main className="grid min-h-dvh place-items-center text-text-hell" role="status" aria-label={t('Einen Moment', 'One moment')}>
        <Kreisel className="size-6" />
      </main>
    );
  }

  const tun = (a: Aktion) => setStand(s => (s ? ablauf(s, a) : s));
  const i = Math.min(stand.schritt, FRAGEN.length - 1);
  const frage = FRAGEN[i]!;
  const gewaehlt = stand.auswahl[frage.id] ?? [];
  const letzte = i === FRAGEN.length - 1;

  function weiter(e?: FormEvent) {
    e?.preventDefault();
    if (speichert || !stand) return;
    const neu = ablauf(stand, { typ: 'weiter' });
    if (fertig(neu)) { void abschliessen(neu, 'fertig'); return; }
    setStand(neu);
  }

  // Enter auf einer Checkbox oder einem Radio sendet sonst nichts ab.
  function taste(e: KeyboardEvent<HTMLFormElement>) {
    const feld = e.target as HTMLInputElement;
    if (e.key === 'Enter' && (feld.type === 'checkbox' || feld.type === 'radio')) weiter(e);
  }

  // Was die Antworten bisher ergeben (Entwurf 1b, 04.10.2026): derselbe
  // Vorschlag wie am Ende des Rundgangs und in „Dein erster Run".
  const bisher = zumSpeichern(stand);
  const vorschlag = ersterLaufVorschlag(bisher);
  const beantwortet = (stand.auswahl.pruefen?.length ?? 0) > 0 || (stand.auswahl.zielgruppe?.length ?? 0) > 0;

  return (
    <div className="min-h-dvh">
      {/* Zwei Spalten ab lg: links die Frage, rechts was die Antworten bringen.
          Darunter eine Spalte wie vorher. */}
      <div className="mx-auto w-full max-w-[36rem] px-4 sm:px-6 lg:grid lg:max-w-[62rem] lg:grid-cols-[minmax(0,36rem)_18.75rem] lg:justify-between lg:gap-12">
        <div className="min-w-0">
          <header className="flex items-center justify-between gap-3 pt-5">
            {i > 0 ? (
              <button
                type="button" onClick={() => tun({ typ: 'zurueck' })} disabled={speichert}
                className="-ml-2 inline-flex cursor-pointer items-center gap-1.5 rounded-md border-0 bg-transparent px-2 py-1.5 text-sm text-text-leise transition-colors duration-150 hover:bg-ebene hover:text-tinte"
              >
                <span aria-hidden>←</span> {t('Zurück', 'Back')}
              </button>
            ) : (
              <Wortmarke className="h-6 sm:h-7" />
            )}
            <button
              type="button" onClick={() => void abschliessen(stand, 'uebersprungen')} disabled={speichert}
              className="-mr-2 cursor-pointer rounded-md border-0 bg-transparent px-2 py-1.5 text-sm text-text-leise underline decoration-linie underline-offset-[3px] transition-colors duration-150 hover:text-tinte hover:decoration-text-hell"
            >
              {aendern ? t('Abbrechen', 'Cancel') : t('Überspringen', 'Skip')}
            </button>
          </header>

          <ol aria-label={t(`Frage ${i + 1} von ${FRAGEN.length}`, `Question ${i + 1} of ${FRAGEN.length}`)} className="m-0 mt-4 flex list-none gap-1.5 p-0">
            {FRAGEN.map((f, n) => (
              <li key={f.id} className="flex-1" aria-current={n === i ? 'step' : undefined}>
                <span className={`block h-1 rounded-full transition-colors duration-300 ${n <= i ? 'bg-marke' : 'bg-linie'}`} />
              </li>
            ))}
          </ol>

          <main className={`overflow-x-clip pt-8 sm:pt-12 sm:pb-12 ${beantwortet ? 'pb-48' : 'pb-28'}`}>
            <form key={i} onSubmit={weiter} onKeyDown={taste}>
              {/* Nur der Inhalt gleitet herein, nicht das Formular: ein Vorfahr mit
                  transform haelt die feste Leiste unten am Formular statt am
                  Bildschirm, und „Weiter" lag bei 340 x 640 unter dem Rand. */}
              <div className={stand.richtung === 'vor' ? 'frage-vor' : 'frage-zurueck'}>
                <div className="text-[0.75rem] font-semibold tracking-[0.07em] text-text-hell uppercase">
                  {t(`Frage ${i + 1} von ${FRAGEN.length}`, `Question ${i + 1} of ${FRAGEN.length}`)}
                </div>
                <fieldset className="m-0 mt-2 min-w-0 border-0 p-0">
                  <legend className="contents">
                    <h1 ref={titel} tabIndex={-1} className="m-0 font-serif text-[1.625rem] leading-[1.15] font-semibold tracking-[-0.015em] text-tinte outline-none sm:text-[2rem]">
                      {frageTitel(frage)}
                    </h1>
                  </legend>
                  <p className="mt-1.5 mb-5 text-sm text-text-leise">{frage.mehrfach ? t('Mehrere Antworten möglich.', 'Several answers possible.') : t('Eine Antwort.', 'One answer.')}</p>

                  {/* Eine Zeile je Antwort (Entwurf 1b): vorher Pillen in ungleichen
                      Reihen, lange Antworten wurden zweizeilige Pillen. */}
                  <div className="grid gap-1.5">
                    {frage.optionen.map((o, n) => {
                      const an = gewaehlt.includes(o.wert);
                      return (
                        <label
                          key={o.wert}
                          style={{ animationDelay: `${120 + n * 40}ms` }}
                          className={`chip-rein ${an ? 'chip-an' : ''} flex min-h-11 cursor-pointer items-center gap-3 rounded-[0.625rem] border px-3 py-2 text-[0.90625rem] sm:min-h-12 sm:px-3.5 sm:py-2.5 sm:text-[0.9375rem] leading-snug shadow-knopf transition-colors duration-150
                            has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-marke
                            ${an ? 'border-marke bg-marke/[0.07] font-medium text-tinte' : 'border-linie bg-flaeche text-text hover:border-linie-stark hover:bg-ebene'}`}
                        >
                          <input
                            type={frage.mehrfach ? 'checkbox' : 'radio'}
                            name={frage.id} value={o.wert} checked={an}
                            onChange={() => tun({ typ: 'waehlen', frage: frage.id, wert: o.wert })}
                            className="peer sr-only"
                          />
                          <span
                            aria-hidden
                            className={`grid size-[1.125rem] shrink-0 place-items-center border-[1.5px] text-[0.6875rem] text-auf-marke transition-colors duration-150 ${
                              frage.mehrfach ? 'rounded-[5px]' : 'rounded-full'} ${
                              an
                                ? (frage.mehrfach ? 'border-knopf bg-knopf' : 'border-marke bg-flaeche shadow-[inset_0_0_0_4px_var(--color-marke)]')
                                : 'border-linie-stark bg-flaeche'}`}
                          >
                            {an && frage.mehrfach ? '✓' : null}
                          </span>
                          <span className="min-w-0">{optionText(o)}</span>
                        </label>
                      );
                    })}
                  </div>

                  {gewaehlt.includes(ANDERES) && (
                    <label className="mt-4 block">
                      <span className="mb-1.5 block text-[0.84375rem] font-medium text-text">
                        {t('Was genau?', 'What exactly?')} <span className="font-normal text-text-hell">(optional)</span>
                      </span>
                      <input
                        value={stand.freitext[frage.id] ?? ''}
                        onChange={e => tun({ typ: 'freitext', frage: frage.id, text: e.target.value })}
                        maxLength={FREITEXT_MAX}
                        className="box-border h-[2.75rem] w-full rounded-lg border border-linie-feld bg-flaeche px-3 text-[0.9375rem] text-text
                                   shadow-[inset_0_1px_1px_rgb(20_18_12/0.04)] outline-none transition-[border-color,box-shadow] duration-150
                                   hover:border-linie-stark focus:border-marke focus:shadow-[0_0_0_3px_rgb(63_138_135/0.16)] focus-visible:outline-none"
                      />
                    </label>
                  )}
                </fieldset>

                {/* Ein Satz auf die Auswahl. aria-live, damit ihn ein Screenreader einmal vorliest. */}
                <p aria-live="polite" className="m-0 mt-4 min-h-6 font-serif text-[1.0625rem] leading-snug text-marke-tief italic">
                  {gewaehlt.length > 0 && <span className="einblenden block">{frageReaktion(frage)}</span>}
                </p>

                {/* Zwischen sm und lg der Satz fuer den Start als schmaler
                    Streifen; am Telefon steht er in der Leiste unten. */}
                {beantwortet && (
                  <p className="mt-3 mb-0 hidden rounded-[0.625rem] border border-dashed border-marke/45 bg-marke/[0.04] px-3 py-2.5 text-[0.8125rem] leading-relaxed text-text-leise sm:block lg:hidden">
                    <span className="font-semibold text-tinte">{t('Dein Start in Claude: ', 'Your start in Claude: ')}</span>{t('„', '“')}{vorschlag.aufgaben[0]!.satz}{t('“', '”')}
                  </p>
                )}
              </div>

              {/* Auf dem Telefon eine feste Leiste unten, wie im Rundgang: „Weiter"
                  stand sonst je Frage an einer anderen Hoehe. Ab sm im Fluss. */}
              <div className="fixed inset-x-0 bottom-0 z-10 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-linie bg-grund/95 px-4 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-sm sm:static sm:mt-6 sm:justify-between sm:gap-y-3 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
                {/* Am Telefon steht der Satz fuer den Start hier, direkt ueber
                    „Weiter" (Entwurf „Fragen C“, Canvas „99takes Onboarding
                    Politur“, gewaehlt von Robert am 12.10.2026). Im Fluss lag
                    er bei 340 x 640 unter dem Rand, und gerade der zeigt, was
                    die Antworten bringen. */}
                {beantwortet && (
                  <p className="einblenden m-0 basis-full rounded-lg bg-blase px-2.5 py-2 sm:hidden">
                    <span className="block text-[0.71875rem] font-semibold tracking-[0.06em] text-marke-tief uppercase">{t('Dein Start in Claude', 'Your start in Claude')}</span>
                    <span className="mt-0.5 line-clamp-2 text-[0.8125rem] leading-snug text-tinte">{t(`„${vorschlag.aufgaben[0]!.satz}“`, `“${vorschlag.aufgaben[0]!.satz}”`)}</span>
                  </p>
                )}
                <p className="m-0 hidden min-w-0 flex-1 basis-48 text-[0.8125rem] leading-relaxed text-text-hell sm:block">
                  {i === 0 ? t('Fünf kurze Fragen, danach eine Minute Rundgang.', 'Five short questions, then a one-minute tour.') : null}
                </p>
                <button
                  type="submit" disabled={speichert}
                  className="inline-flex h-[2.75rem] w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-0 bg-knopf px-6 text-[0.9375rem] font-semibold text-auf-marke
                             shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(20_18_12/0.18)] transition-[background-color,transform] duration-150
                             hover:bg-knopf-hover active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:min-w-36"
                >
                  {speichert ? <><Kreisel />{t('Einen Moment …', 'One moment …')}</> : letzte ? (aendern ? t('Speichern', 'Save') : t('Fertig', 'Done')) : t('Weiter', 'Next')}
                </button>
              </div>
            </form>
          </main>
        </div>

        {/* Was die Antworten bringen (Entwurf 1b, „Fragen nach der Registrierung",
            Claude-Design-Projekt 7f590d08-…, gewaehlt von Robert am 04.10.2026).
            Nur Anzeige: derselbe Vorschlag wie in erster-lauf.ts, nichts davon
            wird zusaetzlich gespeichert. */}
        <aside aria-label={t('Dein Start in Claude', 'Your start in Claude')} className="hidden lg:block lg:pt-[9.5rem]">
          <div className="rounded-[0.875rem] border border-linie bg-flaeche p-5 shadow-karte">
            <div className="text-[0.6875rem] font-semibold tracking-[0.07em] text-text-hell uppercase">{t('Dein Start in Claude', 'Your start in Claude')}</div>
            <div className="mt-1 font-serif text-[1.1875rem] leading-tight font-semibold text-tinte">{t('Damit fängst du an', 'This is how you start')}</div>
            <p className="mt-3 mb-0 rounded-[0.75rem] rounded-br-[0.25rem] bg-blase px-3 py-2.5 text-[0.84375rem] leading-relaxed text-tinte">
              {vorschlag.aufgaben[0]!.satz}
            </p>
            <ul className="m-0 mt-3.5 grid list-none gap-2 p-0 text-[0.8125rem] leading-snug text-text-leise">
              <Punkt da={(stand.auswahl.pruefen?.length ?? 0) > 0}>
                {t('Prüfen: ', 'Test: ')}{antwortText('pruefen', stand.auswahl.pruefen) ?? t('noch offen', 'still open')}
              </Punkt>
              <Punkt da={(stand.auswahl.zielgruppe?.length ?? 0) > 0}>
                {t('Zielgruppe: ', 'Audience: ')}{antwortText('zielgruppe', stand.auswahl.zielgruppe) ?? t('noch offen', 'still open')}
              </Punkt>
            </ul>
            {/* Was es kostet, als zwei Zeilen statt eines Satzes (Feedback
                Testnutzer, 15.10.2026: „Kostet dich: deine Zielgruppe einmalig
                15, ein Vergleich rund 73 Credits, als 1:1 rund 19" musste er
                zweimal lesen, und „1:1" sagt ohne Vorwissen nichts). Die
                Spanne ist dieselbe Rechnung wie in preise.ts: 12 Personas als
                1:1 bis Persona × Job. */}
            <div className="mt-4 border-t border-linie-zart pt-3.5">
              <div className="text-[0.6875rem] font-semibold tracking-[0.07em] text-text-hell uppercase">{t('Was es kostet', 'What it costs')}</div>
              <dl className="m-0 mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 text-[0.8125rem] leading-snug">
                <dt className="text-text-leise">{t('Deine Zielgruppe anlegen', 'Create your audience')}</dt>
                <dd className="m-0 text-right font-semibold whitespace-nowrap text-tinte tabular-nums">{t(<>{EINRICHTUNG} Credits, einmal</>, <>{EINRICHTUNG} credits, once</>)}</dd>
                <dt className="text-text-leise">{t('Ein Vergleich zweier Texte', 'A comparison of two texts')}</dt>
                <dd className="m-0 text-right font-semibold whitespace-nowrap text-tinte tabular-nums">{t(<>{VERGLEICH_1ZU1} bis {VERGLEICH_STANDARD} Credits</>, <>{VERGLEICH_1ZU1} to {VERGLEICH_STANDARD} credits</>)}</dd>
              </dl>
              <p className="m-0 mt-2.5 text-[0.75rem] leading-relaxed text-text-hell">
                {t('Der Preis hängt vom Umfang ab: ob jede Persona den Text einmal liest oder mehrmals, aus verschiedenen Anlässen. Claude nennt ihn dir, bevor etwas gebucht wird, und abgebucht wird zuerst vom Startguthaben.',
                  'The price depends on the scope: whether each persona reads the text once or several times, for different occasions. Claude tells you the price before anything is charged, and it comes out of your starting credits first.')}
              </p>
            </div>
          </div>
          <p className="mx-1 mt-2.5 mb-0 text-[0.75rem] text-text-hell">{t('Die Antworten änderst du später unter Konto.', 'You can change the answers later under Account.')}</p>
        </aside>
      </div>
    </div>
  );
}

/** Die Anzeigetexte der gewaehlten Antworten einer Frage, oder null. */
function antwortText(id: FrageId, werte: string[] | undefined): string | null {
  if (!werte?.length) return null;
  const frage = FRAGEN.find(f => f.id === id)!;
  return werte.map(w => { const o = frage.optionen.find(x => x.wert === w); return o ? optionText(o) : w; }).join(', ');
}

function Punkt({ da, children }: { da: boolean; children: ReactNode }) {
  return (
    <li className="flex items-baseline gap-2">
      <span aria-hidden className={`grid size-[0.9375rem] shrink-0 translate-y-[0.125rem] place-items-center rounded-full text-[0.5625rem] ${da ? 'bg-gut/[0.12] text-gut' : 'border-[1.5px] border-linie-stark'}`}>{da ? '✓' : null}</span>
      <span className="min-w-0">{children}</span>
    </li>
  );
}

// Der Rundgang lebt in Einfuehrung.tsx (seit 29.09.2026 als Geschichte).
export { Rundgang };
